import clone from 'lodash/clone';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import patientServices from 'core/Patient/services';
import { push } from 'react-router-redux';
import turnsServices from './services';
import {
    call,
    put,
    select,
    takeEvery
} from 'redux-saga/effects';
import {
    parseSelect,
    timezoneOffsetString
} from 'shared/utils';
import {
    parseSelectedTurn,
    parseSingleTurn,
    turnsActions
} from './';

function* _cancelTurn ({ payload }) {
    try {
        yield put(turnsActions.setTurnsListLoading(true));
        yield put(turnsActions.setTurnsStatus('deleting'));
        const { id, ...body } = payload;
        const { data, data: { id: updatedId } } = yield call(turnsServices.deleteAppointment, { id }, body);
        const { turns: { list, list: { turns } } } = yield select();
        yield call([turns, 'splice'], turns.findIndex(({ id }) => id === updatedId), 1, data);
        yield put(turnsActions.setTurnsList({ ...list, turns }));
    } catch (err) {

    } finally {
        yield put(turnsActions.setTurnsStatus(''));
        yield put(turnsActions.setTurnsListLoading(false));
    }
}

function* _createAgenda ({ payload }) {
    try {
        const body = clone(payload);
        const { doctor_id } = body;
        if (!doctor_id) {
            delete body.doctor_id;
        }
        const { data } = yield call(turnsServices.postAgenda, null, body);
        yield put(push(`${ i18n.t('/agenda') }?professional=${ doctor_id }`));
        return data;
    } catch (e) {
        const { response: { data: { message }, status } } = e;
        if (status === 422 && message === 'existing_agenda') {
            yield put(turnsActions.setTurnsAgendaError(i18n.t('El rango de fechas se superpone con otro ya existente')));
        }
    }
}

function* _createAppointmentType ({ payload }) {
    try {
        const { data } = yield call(turnsServices.postAppointmentType, null, payload);
        yield put(push(i18n.t('/consultas/tipos/listar')));
        return data;
    } catch (e) {

    }
}

function* _createAvailableTime ({ payload }) {
    try {
        yield put(turnsActions.setTurnsAvailableTimesLoading(true));
        const { data } = yield call(turnsServices.postAvailableTime, null, payload);
        const { turns: { availableTimes } } = yield select();
        const newData = clone(availableTimes);
        newData.push(data);
        yield put(turnsActions.setTurnsAvailableTimes(newData));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsAvailableTimesLoading(false));
    }
}

function* _createExternalAppointment ({ payload: { patientId, body } }) {
    try {
        yield put(turnsActions.setTurnsExternalAppointmentLoading(true));
        const { data: {
            birth_date,
            email,
            first_name,
            gender,
            identification,
            last_name
        } } = yield call(patientServices.fetchPatientData, { patient_id: patientId });
        const { data } = yield call(turnsServices.postExternalAppointment, null, {
            ...body,
            birth_date: moment(birth_date).format('YYYY-MM-DD'),
            email,
            first_name,
            gender,
            identification,
            last_name
        });
        yield put(turnsActions.setTurnsExternalAppointment(data));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsExternalAppointmentLoading(false));
    }
}

function* _createTurn ({ payload }) {
    try {
        yield put(turnsActions.setTurnsStatus('creating'));
        const { newPatient } = payload;
        if (!!newPatient && !isEmpty(newPatient)) {
            const { doctor_id, specialty_id } = payload;
            const { data: { patient: { patient_id } } } = yield call(patientServices.postPatient, null, {
                ...newPatient,
                ...(doctor_id && { doctors_related: [doctor_id] }),
                method: 'turn',
                ...(specialty_id && !doctor_id && { specialty_id })
            });
            delete payload.newPatient;
            yield call(turnsServices.postAppointment, null, { ...payload, patient_id });
            yield put(turnsActions.setTurnsStatus('created'));
        } else {
            delete payload.newPatient;
            yield call(turnsServices.postAppointment, null, payload);
            yield put(turnsActions.setTurnsStatus('created'));
        }
    } catch ({ response: { data: { email, identification } } }) {
        yield put(turnsActions.setTurnsCreationError({
            ...(!!email && { email: email[0] === 'SHOULD_BE_EMAIL' ? i18n.t('Email inválido') : i18n.t('Error al registrar el correo') }),
            ...(!!identification && { identification: identification[0] === 'IDENTIFICATION_ALREADY_IN_USE' ? i18n.t('Ya registrado') : i18n.t('Error al registrar el DNI') })
        }));
        yield put(turnsActions.setTurnsStatus('error'));
    } finally {

    }
}

function* _createUnavailableTime ({ payload }) {
    try {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(true));
        const { data } = yield call(turnsServices.postUnavailableTime, null, payload);
        const { turns: { unavailableTimes } } = yield select();
        const newData = clone(unavailableTimes);
        newData.push(data);
        yield put(turnsActions.setTurnsUnavailableTimes(newData));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(false));
    }
}

function* _deleteAgenda ({ payload: id }) {
    try {
        const { data, data: { doctor_id } } = yield call(turnsServices.deleteAgenda, { id });
        yield put(push(`${ i18n.t('/agenda') }/${ doctor_id }`));
        return data;
    } catch (e) {

    }
}

function* _deleteAppointmentType ({ payload: id }) {
    try {
        const { data } = yield call(turnsServices.deleteAppointmentType, { id });
        yield put(push(i18n.t('/consultas/tipos/listar')));
        return data;
    } catch (e) {

    }
}

function* _deleteAvailableTime ({ payload: id }) {
    try {
        yield put(turnsActions.setTurnsAvailableTimesLoading(true));
        const { data } = yield call(turnsServices.deleteAvailableTime, { id });
        const { turns: { availableTimes } } = yield select();
        const newData = clone(availableTimes).filter(({ id: elemId }) => id !== elemId);
        yield put(turnsActions.setTurnsAvailableTimes(newData));
        return data;
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsAvailableTimesLoading(false));
    }
}

function* _deleteUnavailableTime ({ payload: id }) {
    try {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(true));
        const { data } = yield call(turnsServices.deleteUnavailableTime, { id });
        const { turns: { unavailableTimes } } = yield select();
        const newData = clone(unavailableTimes).filter(({ id: elemId }) => id !== elemId);
        yield put(turnsActions.setTurnsUnavailableTimes(newData));
        return data;
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(false));
    }
}

function* _editTurn ({ payload }) {
    try {
        yield put(turnsActions.setTurnsListLoading(true));
        yield put(turnsActions.setTurnsStatus('editing'));
        const { id, ...body } = payload;
        yield call(turnsServices.putAppointment, { id }, body);
    } catch (err) {

    } finally {
        yield put(turnsActions.setTurnsStatus(''));
        yield put(turnsActions.setTurnsListLoading(false));
    }
}

function* _getAgenda ({ payload: id }) {
    try {
        const { data } = yield call(turnsServices.getAgenda, { id });
        yield put(turnsActions.setTurnsAgenda({
            ...data,
            office_id: data?.office?.id
        }));
    } catch (e) {
        
    }
}

function* _getAgendas ({ payload: doctor_id }) {
    try {
        yield put(turnsActions.setTurnsAgendasLoading(true));
        const { data } = doctor_id ? yield call(turnsServices.getDoctorAgendas, { doctor_id }) : yield call(turnsServices.getAllAgendas);
        yield put(turnsActions.setTurnsAgendas(data));
    } catch (e) {
        
    } finally {
        yield put(turnsActions.setTurnsAgendasLoading(false));
    }
}

function* _getAppointmentType ({ payload }) {
    try {
        const { data } = yield call(turnsServices.getAppointmentType, { id: payload });
        yield put(turnsActions.setTurnsAppointmentType(data));
    } catch (e) {

    } finally {

    }
}

function* _getAppointmentTypes ({ payload }) {
    try {
        yield put(turnsActions.setTurnsAppointmentTypesLoading(true));
        const { data } = yield call(turnsServices.getAppointmentTypes, null, payload);
        yield put(turnsActions.setTurnsAppointmentTypes(parseSelect(data)));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsAppointmentTypesLoading(false));
    }
}

function* _getAvailableAgenda ({ payload }) {
    try {
        yield put(turnsActions.setTurnsAvailableAgendaLoading(true));
        yield put(turnsActions.setTurnsListLoading(true));
        const { doctor_id, specialty_id, ...params } = payload;
        const { data: agendas } = doctor_id
            ? yield call(turnsServices.getAvailableAgenda, { doctor_id }, params)
            : {
                data: {
                    agendas: [],
                    availables: [],
                    unavailables: []
                }
            };
        params.timezone = timezoneOffsetString();
        const { data } = doctor_id
            ? yield call(turnsServices.getDoctorAppointments, { doctor_id }, params)
            : yield call(turnsServices.getSpecialtyAppointments, { specialty_id }, params);
        const { appointments, recurrences = [] } = doctor_id ? data : { appointments: data };
        yield put(turnsActions.setTurnsFilters(payload));
        yield put(turnsActions.setTurnsAvailableAgenda(agendas));
        const turns = [...appointments, ...recurrences];
        yield put(turnsActions.setTurnsList({
            turns,
            unavailables: agendas?.unavailables ?? []
        }));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsListLoading(false));
        yield put(turnsActions.setTurnsAvailableAgendaLoading(false));
    }
}

function* _getAvailableTimes ({ payload: id }) {
    try {
        yield put(turnsActions.setTurnsAvailableTimesLoading(true));
        if (!id) {
            yield put(turnsActions.setTurnsAvailableTimes([]));
            return;
        }
        const { available_times } = yield call(turnsServices.getAvailableTimes, null, { doctor_id: id });
        yield put(turnsActions.setTurnsAvailableTimes(available_times));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsAvailableTimesLoading(false));
    }
}

function* _getSelectedTurn ({ payload: { id } }) {
    try {
        yield put(turnsActions.setTurnsSelectedTurnLoading(true));
        const { data } = yield call(turnsServices.fetchAppointment, { id });
        yield put(turnsActions.setTurnsSelectedTurn(parseSelectedTurn(data)));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsSelectedTurnLoading(false));
    }
}

function* _getSchedule ({ payload: { doctor_id } }) {
    try {
        yield put(turnsActions.setTurnsScheduleLoading(true));
        yield put(turnsActions.getAgendas(doctor_id));
        yield put(turnsActions.getAvailableTimes(doctor_id));
        yield put(turnsActions.getUnavailableTimes(doctor_id));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsScheduleLoading(false));
    }
}

function* _getTurnAudit ({ payload }) {
    try {
        yield put(turnsActions.setTurnsAuditLoading(true));
        const { data } = yield call(turnsServices.fetchAppointmentAudit, null, { appointment_id: payload });
        yield put(turnsActions.setTurnsAudit(data));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsAuditLoading(false));
    }
}

function* _getTurnsDownloadList ({ payload }) {
    try {
        yield put(turnsActions.setTurnsDownloadStatus('loading'));
        const { data } = yield call(turnsServices.getAllAppointments, null, payload);
        yield put(turnsActions.setTurnsDownloadList(data));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsDownloadStatus('done'));
    }
}

function* _getUnavailableTimes ({ payload: id }) {
    try {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(true));
        if (!id) {
            yield put(turnsActions.setTurnsUnavailableTimes([]));
            return;
        }
        const { unavailable_times } = yield call(turnsServices.getUnavailableTimes, null, { doctor_id: id });
        yield put(turnsActions.setTurnsUnavailableTimes(unavailable_times));
    } catch (e) {

    } finally {
        yield put(turnsActions.setTurnsUnavailableTimesLoading(false));
    }
}

function* _handleAppointmentsDelete ({ payload }) {
    try {
        const { turns: { eventTurns } } = yield select();
        const filteredList = eventTurns.filter(({ resource: { id } }) => !payload.includes(id));
        yield put(turnsActions.setTurnsEventTurns(filteredList));
    } catch (e) {

    }
}

function* _updateAgenda ({ payload: { body, id } }) {
    try {
        const { doctor_id } = body;
        body.doctor_id = !!doctor_id ? doctor_id : 0;
        yield call(turnsServices.putAgenda, { id }, body);
        yield put(push(`${ i18n.t('/agenda') }?professional=${ doctor_id }`));
    } catch (e) {
        const message = e?.response?.data?.message;
        if (message === 'existing_agenda') {
            yield put(turnsActions.setTurnsAgendaError(i18n.t('El rango de fechas se superpone con otro ya existente')));
        }
    }
}

function* _updateAppointmentType ({ payload: { body, id } }) {
    try {
        const { data } = yield call(turnsServices.putAppointmentType, { id }, body);
        yield put(push(i18n.t('/consultas/tipos/listar')));
        return data;
    } catch (err) {
    }
}

function* _updateSingleAppointment ({ payload }) {
    try {
        const { turns: { eventTurns, selectedDoctorId, selectedSpecialtyId, selectedOfficeId } } = yield select();
        const { id: appointmentId } = payload;
        const newTurns = [...eventTurns];
        const turnsIndex = eventTurns.findIndex(({
            resource: { id: turn_id }
        }) => appointmentId === turn_id);
        if (payload?.doctor?.id === selectedDoctorId || (!selectedDoctorId && payload?.specialty?.id === selectedSpecialtyId)) {
            if (turnsIndex >= 0) {
                const { resource: { same_office } } = newTurns[turnsIndex];
                newTurns[turnsIndex] = parseSingleTurn({ ...payload, same_office });
                yield put(turnsActions.setTurnsEventTurns(newTurns));
            } else {
                const same_office = payload?.office?.id === selectedOfficeId;
                newTurns.push(parseSingleTurn({ ...payload, same_office }));
                yield put(turnsActions.setTurnsEventTurns(newTurns));
            }
        }
    } catch (e) {

    }
}

export default [
    takeEvery(turnsActions.cancelTurn.type, _cancelTurn),
    takeEvery(turnsActions.createAgenda.type, _createAgenda),
    takeEvery(turnsActions.createAppointmentType.type, _createAppointmentType),
    takeEvery(turnsActions.createAvailableTime.type, _createAvailableTime),
    takeEvery(turnsActions.createExternalAppointment.type, _createExternalAppointment),
    takeEvery(turnsActions.createTurn.type, _createTurn),
    takeEvery(turnsActions.createUnavailableTime.type, _createUnavailableTime),
    takeEvery(turnsActions.deleteAgenda.type, _deleteAgenda),
    takeEvery(turnsActions.deleteAppointmentType.type, _deleteAppointmentType),
    takeEvery(turnsActions.deleteAvailableTime.type, _deleteAvailableTime),
    takeEvery(turnsActions.deleteUnavailableTime.type, _deleteUnavailableTime),
    takeEvery(turnsActions.editTurn.type, _editTurn),
    takeEvery(turnsActions.getAgenda.type, _getAgenda),
    takeEvery(turnsActions.getAgendas.type, _getAgendas),
    takeEvery(turnsActions.getAppointmentType.type, _getAppointmentType),
    takeEvery(turnsActions.getAppointmentTypes.type, _getAppointmentTypes),
    takeEvery(turnsActions.getAvailableAgenda.type, _getAvailableAgenda),
    takeEvery(turnsActions.getAvailableTimes.type, _getAvailableTimes),
    takeEvery(turnsActions.getSchedule.type, _getSchedule),
    takeEvery(turnsActions.getSelectedTurn.type, _getSelectedTurn),
    takeEvery(turnsActions.getTurnAudit.type, _getTurnAudit),
    takeEvery(turnsActions.getTurnsDownloadList.type, _getTurnsDownloadList),
    takeEvery(turnsActions.getUnavailableTimes.type, _getUnavailableTimes),
    takeEvery(turnsActions.handleAppointmentsDelete.type, _handleAppointmentsDelete),
    takeEvery(turnsActions.updateAgenda.type, _updateAgenda),
    takeEvery(turnsActions.updateAppointmentType.type, _updateAppointmentType),
    takeEvery(turnsActions.updateSingleAppointment.type, _updateSingleAppointment)
];
